import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import './HuePickerMolecule.scss';

export interface HuePickerMoleculeProps {
  value: number;
  onChange: (hue: number) => void;
  className?: string;
  /**
   * Replaces the `NNN°` readback. The glyph picker's colour tab shows the hue
   * as "not in use" until the glyph's colour is actually a hue — the slider has
   * a position either way, and a bare `210°` would claim a colour that is not
   * applied.
   */
  valueText?: string;
  /** Contract datum for the slider — see TypeFieldRowMolecule's `datum`. */
  sliderDatum?: string;
  /** Contract datum for the value readback. */
  valueDatum?: string;
  /**
   * The nine preset squares. On for the marketplace publisher, where picking a
   * brand hue IS the task; off where the hue is the third-choice path behind a
   * theme token (the glyph picker's Colour tab), and nine saturated squares
   * would shout down the option that is meant to be taken.
   */
  showPresets?: boolean;
  /**
   * Called once a hue is chosen: when the slider is released (or stepped by a key) and when a
   * preset is picked. `onChange` still follows the slider as it moves, so a caller that saves each
   * choice saves here and previews in `onChange`.
   */
  onChangeEnd?: (hue: number) => void;
  /** Makes the picker inert, keeping it focusable, with this reason as its tooltip. */
  disabledReason?: string;
}

const HUE_PRESETS = [0, 30, 60, 120, 180, 210, 270, 300, 330];

const HuePickerMolecule: React.FC<HuePickerMoleculeProps> = ({
  value,
  onChange,
  className = '',
  valueText,
  sliderDatum,
  valueDatum,
  showPresets = true,
  onChangeEnd,
  disabledReason,
}) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLInputElement>(null);
  const disabled = !!disabledReason;

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      onChange(Number.parseInt(e.target.value, 10));
    },
    [onChange, disabled]
  );

  // React's onChange follows the `input` event; the native `change` event is the one a range input
  // fires on release (and on each keyboard step), which is when a hue has been chosen.
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || !onChangeEnd || disabled) return undefined;
    const commit = () => onChangeEnd(Number.parseInt(slider.value, 10));
    slider.addEventListener('change', commit);
    return () => slider.removeEventListener('change', commit);
  }, [onChangeEnd, disabled]);

  const pickPreset = (hue: number) => {
    if (disabled) return;
    onChange(hue);
    onChangeEnd?.(hue);
  };

  const classes = ['marketplace-hue-picker', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="group" aria-label={t('marketplace.publishers.huePicker.ariaLabel')}>
      {showPresets && (
      <div className="marketplace-hue-picker__presets">
        {HUE_PRESETS.map((hue) => (
          <button
            key={hue}
            type="button"
            className={[
              'marketplace-hue-picker__preset',
              value === hue ? 'marketplace-hue-picker__preset--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ background: `hsl(${hue}, 65%, 50%)` }}
            onClick={() => pickPreset(hue)}
            aria-label={t('marketplace.publishers.huePicker.presetAriaLabel', { hue })}
            aria-pressed={value === hue}
            aria-disabled={disabled || undefined}
            title={disabledReason}
          />
        ))}
      </div>
      )}
      <div className="marketplace-hue-picker__slider-row">
        <div
          className="marketplace-hue-picker__preview"
          style={{
            background: `linear-gradient(135deg, hsl(${value}, 60%, 60%), hsl(${value}, 70%, 38%))`,
          }}
          aria-hidden="true"
        />
        <input
          ref={sliderRef}
          type="range"
          className="marketplace-hue-picker__slider"
          min={0}
          max={359}
          value={value}
          onChange={handleSliderChange}
          aria-label={t('marketplace.publishers.huePicker.sliderAriaLabel')}
          aria-disabled={disabled || undefined}
          title={disabledReason}
          data-datum={sliderDatum}
        />
        <span className="marketplace-hue-picker__value" data-datum={valueDatum} data-kind={valueDatum ? 'text' : undefined}>
          {valueText ?? `${value}°`}
        </span>
      </div>
    </div>
  );
};

HuePickerMolecule.displayName = 'HuePickerMolecule';

export default HuePickerMolecule;
